import { ContentViews } from "pages/[orgId]/explore/lib/useContentView";
import { useMemo, useState } from "react";
import { SearchState } from "src/search/types";

export type QuestionQuery = {
  state: {
    params: SearchState;
    viz: {
      value: ContentViews;
      config: any;
    };
  };
  name: string;
  label: JSX.Element | string;
  icon: JSX.Element | string;
  key: string;
  value: JSX.Element | string;
  disabled?: boolean;
  tags?: string[];
};
export type Question = {
  label: string;
  options?: QuestionQuery[];
};

type FilterableAttribute =
  | "select"
  | "groupBy"
  | "visualization"
  | "label"
  | "tag"
  | "duration";

interface ParsedSearch {
  attribute: FilterableAttribute;
  value: string;
}

function parseSearchQuery(query: string): ParsedSearch[] {
  // Handle empty query
  if (!query.trim()) return [];

  const parts = query.split(" ");

  return parts.map((part) => {
    // Check if part contains attribute specification (e.g., "select:Event")
    const colonIndex = part.indexOf(":");
    if (colonIndex > 0) {
      const attribute = part.slice(0, colonIndex);
      const value = part.slice(colonIndex + 1).replace(/^"(.*)"$/, "$1"); // Remove quotes if present
      return {
        attribute: attribute.toLowerCase() as FilterableAttribute,
        value: normalizeTag(value),
      };
    }

    // If no colon, treat as name search
    return {
      attribute: "label",
      value: part.replace(/^"(.*)"$/, "$1").toLowerCase(),
    };
  });
}

/**
 * Adds a new search term to an existing search string, deduping any redundant terms
 * for the same attribute.
 */
export function addSearchTerm(currentSearch: string, newTerm: string): string {
  const currentTerms = parseSearchQuery(currentSearch);
  const [newParsedTerm] = parseSearchQuery(newTerm);

  if (!newParsedTerm) return currentSearch;

  // Filter out existing terms with the same attribute and value
  const deduped = currentTerms.filter((term) => {
    if (term.attribute !== newParsedTerm.attribute) return true;
    return term.value !== newParsedTerm.value;
  });

  // Add the new term
  deduped.push(newParsedTerm);

  // Reconstruct the search string
  return deduped
    .map((term) => {
      if (term.attribute === "label") {
        return term.value;
      }
      return `${term.attribute}:${term.value}`;
    })
    .join(" ");
}

export function normalizeTag(value: string): string {
  return value.toLowerCase().replace(/\s+/g, "_");
}

export function useQuestionFilter(initialQuestions: Question[]) {
  const [searchValue, setSearchValue] = useState("");

  const filteredQuestions = useMemo(() => {
    const parsedSearches = parseSearchQuery(searchValue);

    if (parsedSearches.length === 0) {
      return initialQuestions;
    }

    return initialQuestions
      .map((question) => {
        if (!("options" in question)) return question;

        return {
          ...question,
          options: question.options?.filter((option) =>
            parsedSearches.every((search) => {
              const { attribute, value } = search;
              const normalizedValue = normalizeTag(value);

              if (attribute === "tag") {
                return option.tags?.some((tag) =>
                  normalizeTag(tag).includes(normalizedValue)
                );
              }
              if (!option.state?.params) return false;

              switch (attribute) {
                case "select": {
                  return option.state.params.select?.some((item) =>
                    normalizeTag(item).includes(normalizedValue)
                  );
                }
                case "visualization": {
                  return normalizeTag(option.state.viz?.value || "").includes(
                    normalizedValue
                  );
                }
                case "duration": {
                  return normalizeTag(
                    option.state.params.duration || ""
                  ).includes(normalizedValue);
                }
                default: {
                  // filter on label attribute
                  return normalizeTag(option.name || "").includes(
                    normalizedValue
                  );
                }
              }
            })
          ),
        };
      })
      .filter(
        (question) =>
          !("options" in question) ||
          (question.options && question.options.length > 0)
      );
  }, [initialQuestions, searchValue]);

  const addFilter = (newValue: string) => {
    // If the value contains a colon, treat it as a structured filter
    if (newValue.includes(":")) {
      setSearchValue((prevSearch) => addSearchTerm(prevSearch, newValue));
    } else {
      // For free text, just set the value directly
      setSearchValue(newValue);
    }
  };

  const clearFilters = () => {
    setSearchValue("");
  };

  const removeFilter = (attribute: FilterableAttribute, value: string) => {
    const currentTerms = parseSearchQuery(searchValue);
    const filtered = currentTerms.filter(
      (term) => !(term.attribute === attribute && term.value === value)
    );

    setSearchValue(
      filtered
        .map((term) => {
          const value = term.value.includes(" ")
            ? `"${term.value}"`
            : term.value;
          return `${term.attribute}:${value}`;
        })
        .join(" ")
    );
  };

  const toggleFilter = (searchTerm: string) => {
    console.log("Toggling filter:", searchTerm);
    const [newTerm] = parseSearchQuery(searchTerm);
    if (!newTerm) return;

    const currentTerms = parseSearchQuery(searchValue);
    const termExists = currentTerms.some(
      (term) =>
        term.attribute === newTerm.attribute &&
        term.value.toLowerCase() === newTerm.value.toLowerCase()
    );

    if (termExists) {
      const filtered = currentTerms.filter(
        (term) =>
          !(
            term.attribute === newTerm.attribute &&
            term.value.toLowerCase() === newTerm.value.toLowerCase()
          )
      );

      setSearchValue(
        filtered
          .map((term) => {
            // For free text (label attribute), just return the value
            if (term.attribute === "label") {
              return term.value;
            }
            const value = term.value.includes(" ")
              ? `"${term.value}"`
              : term.value;
            return `${term.attribute}:${value}`;
          })
          .join(" ")
      );
    } else {
      addFilter(searchTerm);
    }
  };

  const parseAndApplySearch = (searchString: string) => {
    const parsedTerms = parseSearchQuery(searchString);
    const formattedSearch = parsedTerms
      .map((term) => {
        // For free text (label attribute), just return the value
        if (term.attribute === "label") {
          return term.value;
        }
        const value = term.value.includes(" ") ? `"${term.value}"` : term.value;
        return `${term.attribute}:${value}`;
      })
      .join(" ");

    setSearchValue(formattedSearch);
  };

  return {
    filteredQuestions,
    searchValue,
    setSearch: setSearchValue,
    addFilter,
    removeFilter,
    clearFilters,
    toggleFilter,
    parseAndApplySearch,
    activeFilters: parseSearchQuery(searchValue),
  };
}

// Example usage:
/*
const filteredQuestions = useQuestionFilter(questions, 'select:Event name:weekly');
// Returns questions that:
// - Have "Event" in their select attributes AND
// - Have "weekly" in their name

const examples = [
  'select:Event',                    // Questions selecting Event entity
  'visualization:line name:traffic', // Line charts with "traffic" in the name
  'select:Event groupBy:time',       // Event queries grouped by time
  'name:"Daily Report"',            // Exact phrase match in name
  'select:Event name:week',         // Event queries with "week" in the name
];
*/
